import { Formik, Field, Form, FormikHelpers, FormikProps } from 'formik';
import { NumericFormat } from 'react-number-format';
import {
  dropdownOptionsMap,
  fieldsAndFriendlyLabels,
} from '@/components/JobBasicInfo/JobBasicInfo.utils';
import Button from '@/trio-ui/Button';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TagSelector from '@/trio-ui/TagSelector';
import { ValuesWithLabel } from '@/types';
import { JobStart, JobStatus } from '@/types/api';
import { yearsOfExperienceOptions } from '@/utils/dropdownOptions';
import { buildErrorMessage } from '@/utils/formsValidation';
import { creatJobSchema } from '@/utils/jobs';
import * as Styles from './NewJobForm.styles';
import { getFormattedCurrencyOriginalValue } from '../JobsList/JobsList.utils';

export const defaultValues = {
  title: '',
  account: '',
  status: JobStatus.ON_HOLD,
  whenToStart: JobStart.IMMEDIATELY,
  salaryRangeMin: 3000,
  salaryRangeMax: 6000,
  jobLink: '',
  developerType: '',
  techs: [],
  numberOfDevs: 1,
  experience: yearsOfExperienceOptions[2],
};

export type ValuesType = typeof defaultValues;

export type NewJobFormComponentType = {
  techStacksOptions: ValuesWithLabel<string>[];
  partnerOptions: ValuesWithLabel<string>[];
  scrollToFieldWithError: () => void;
  onDirtyForm: (isDirty: boolean) => void;
  getTitle: (values: ValuesType) => string;
  getPartnerName: (values: ValuesType) => string;
  onHideJobFormClick: () => void;
  isFormDisabled?: boolean;
  onSubmitForm: (values: ValuesType, actions: FormikHelpers<any>) => void;
};

const NewJobFormComponent = function ({
  scrollToFieldWithError,
  techStacksOptions,
  getTitle,
  getPartnerName,
  partnerOptions,
  onDirtyForm,
  onHideJobFormClick,
  isFormDisabled,
  onSubmitForm,
  ...props
}: NewJobFormComponentType): JSX.Element {
  const handleSalaryChange = (
    fieldName: string,
    event: any,
    form: FormikProps<ValuesType>
  ): void => {
    const numberedValue = getFormattedCurrencyOriginalValue(event.target.value);
    form.setFieldValue(fieldName, numberedValue);
  };

  return (
    <div data-testid="new-job-form" {...props}>
      <Formik
        validationSchema={creatJobSchema}
        initialValues={defaultValues}
        onSubmit={(e, a) => onSubmitForm(e, a)}
      >
        {(form) => {
          onDirtyForm(form.dirty);

          return (
            <Form>
              <>
                <Styles.PageHeader>
                  <Styles.Title data-testid="new-job-title">
                    {!!form.values.title && getTitle(form.values)}
                  </Styles.Title>

                  <Styles.PartnerName data-testid="new-job-partner">
                    {!!form.values.account && (
                      <>
                        at <strong> {getPartnerName(form.values)}</strong>
                      </>
                    )}
                  </Styles.PartnerName>
                </Styles.PageHeader>

                <Styles.Box className="new-job-form-area">
                  <Styles.Field>
                    <Field name="title" data-testid="input-title">
                      {({ field }) => {
                        return (
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label={fieldsAndFriendlyLabels.title}
                            name="title"
                            errorMessage={buildErrorMessage(form.touched.title, form.errors.title)}
                            value={field.value}
                          />
                        );
                      }}
                    </Field>
                  </Styles.Field>

                  <Field name="account">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            optionsWithLabel={partnerOptions}
                            name="account"
                            defaultValue={field.value}
                            label={fieldsAndFriendlyLabels.account}
                            errorMessage={buildErrorMessage(
                              form.touched.account,
                              form.errors.account
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Field name="status">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={dropdownOptionsMap[field.name]}
                            name="status"
                            defaultValue={field.value}
                            label={fieldsAndFriendlyLabels.status}
                            errorMessage={buildErrorMessage(
                              form.touched.status,
                              form.errors.status
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Field name="whenToStart">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={dropdownOptionsMap[field.name]}
                            name="whenToStart"
                            defaultValue={field.value}
                            label={fieldsAndFriendlyLabels.whenToStart}
                            errorMessage={buildErrorMessage(
                              form.touched.whenToStart,
                              form.errors.whenToStart
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Styles.DoubleInputRow>
                    <Field name="salaryRangeMin" key="salaryRangeMin">
                      {({ field }) => (
                        <NumericFormat
                          customInput={InputText}
                          thousandSeparator=","
                          prefix="$ "
                          onChangeHandler={() => {}}
                          onValueChange={(_, sourceInfo) =>
                            handleSalaryChange(field.name, sourceInfo.event, form)
                          }
                          onBlur={form.submitForm}
                          label={fieldsAndFriendlyLabels.salaryRange}
                          isLabelAboveField
                          name={field.name}
                          value={field.value}
                          errorMessage={buildErrorMessage(
                            form.touched.salaryRangeMin,
                            form.errors.salaryRangeMin
                          )}
                        />
                      )}
                    </Field>

                    <span>&nbsp;to&nbsp;</span>

                    <Field name="salaryRangeMax" key="salaryRangeMax">
                      {({ field }) => (
                        <NumericFormat
                          customInput={InputText}
                          thousandSeparator=","
                          prefix="$ "
                          onChangeHandler={() => {}}
                          onValueChange={(_, sourceInfo) =>
                            handleSalaryChange(field.name, sourceInfo.event, form)
                          }
                          onBlur={form.submitForm}
                          label=" "
                          isLabelAboveField
                          name={field.name}
                          value={field.value}
                          errorMessage={buildErrorMessage(
                            form.touched.salaryRangeMax,
                            form.errors.salaryRangeMax
                          )}
                        />
                      )}
                    </Field>
                  </Styles.DoubleInputRow>

                  <Field name="jobLink" data-testid="input-job-link">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label={fieldsAndFriendlyLabels.jobLink}
                            name="jobLink"
                            errorMessage={buildErrorMessage(
                              form.touched.jobLink,
                              form.errors.jobLink
                            )}
                            value={field.value}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Field name="developerType">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={dropdownOptionsMap[field.name]}
                            name="developerType"
                            defaultValue={field.value}
                            label={fieldsAndFriendlyLabels.developerType}
                            errorMessage={buildErrorMessage(
                              form.touched.developerType,
                              form.errors.developerType
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Field name="techs">
                    {({ field }) => {
                      const tagSelectorLimit = form.values.developerType.includes('Full Stack')
                        ? 6
                        : 3;

                      return (
                        <Styles.Field>
                          <TagSelector
                            optionsWithLabel={techStacksOptions}
                            selectorLimit={tagSelectorLimit}
                            name={field.name}
                            defaultValue={field.value}
                            label={fieldsAndFriendlyLabels.techs}
                            isLabelAboveField
                            errorMessage={buildErrorMessage(form.touched.techs, form.errors.techs)}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Field name="numberOfDevs">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <InputText
                            isLabelAboveField
                            onChangeHandler={form.handleChange}
                            label={fieldsAndFriendlyLabels.numberOfDevs}
                            name="numberOfDevs"
                            errorMessage={buildErrorMessage(
                              form.touched.numberOfDevs,
                              form.errors.numberOfDevs
                            )}
                            value={field.value}
                            type="number"
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Field name="experience">
                    {({ field }) => {
                      return (
                        <Styles.Field>
                          <DropDown
                            isLabelAboveField
                            options={dropdownOptionsMap[field.name]}
                            name="experience"
                            defaultValue={field.value}
                            label={fieldsAndFriendlyLabels.experience}
                            errorMessage={buildErrorMessage(
                              form.touched.experience,
                              form.errors.experience
                            )}
                            onChangeDropDown={form.handleChange}
                          />
                        </Styles.Field>
                      );
                    }}
                  </Field>

                  <Styles.Footer>
                    <Button
                      onClick={() => {
                        form.validateForm().then(() => {
                          scrollToFieldWithError();
                        });
                      }}
                      disabled={form.isSubmitting || isFormDisabled}
                      data-testid="new-job-submit-btn"
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      disabled={form.isSubmitting || isFormDisabled}
                      variantStyle="outline"
                      data-testid="new-job-cancel-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        form.resetForm();
                        onHideJobFormClick();
                      }}
                    >
                      Cancel
                    </Button>
                  </Styles.Footer>
                </Styles.Box>
              </>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewJobFormComponent;
